import { flushSync } from 'react-dom';

/** Minimal edit-slot shape — kept local to avoid importing the table types barrel (circular dep). */
type InlineEditSlot = { rowIndex: number | null; columnId: string | null };

/** Subset of editable context read by handoff handlers (DL-60610). */
type ActiveEditableSlotContext = {
  editableState?: InlineEditSlot | null;
  editableStateRef?: { current: InlineEditSlot | null };
} | null;

/** Read the live shared edit slot (ref wins over render closure — DL-60610). */
export const readActiveEditableSlot = (
  ctx: ActiveEditableSlotContext | undefined
): InlineEditSlot | null => {
  if (!ctx) return null;
  // When the ref is present, trust it exclusively — a cleared slot is `null`,
  // and falling back to `editableState` would read a stale memo closure.
  if (ctx.editableStateRef) return ctx.editableStateRef.current ?? null;
  return ctx.editableState ?? null;
};

/**
 * Commit the currently-focused inline editor before another cell claims the
 * shared editing slot.
 *
 * Popup cells (dropdown / multiselect / date) activate on `mousedown` so they
 * open on the first click while another cell is mid-edit (DL-60264). But a
 * `mousedown` on a non-focusable cell does not blur the focused text/number
 * input — that input only blurs later, once the popup autofocuses, by which time
 * the editing slot has moved and the input has unmounted, so its `onBlur`-save
 * never runs and the typed value is lost.
 *
 * Calling this at the start of the popup cell's activate handler blurs the
 * focused editor synchronously, so its `onBlur` → save → onUpdate commits first
 * (the "blur before microtask" ordering DL-60264 already supports: the outgoing
 * cell still owns the slot, clears it, then the microtask sets the target — so
 * the value is saved and the popup still opens on the first click).
 */
export const commitActiveEditor = (): void => {
  const el = typeof document !== 'undefined' ? document.activeElement : null;
  if (!(el instanceof HTMLElement)) return;
  const isTextEditor = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  if (isTextEditor) {
    el.blur();
  }
};

/**
 * Prepare a popup-cell handoff (dropdown / multiselect / date) on `mousedown`.
 *
 * Text/number → popup is covered by {@link commitActiveEditor} alone. Popup →
 * popup (DL-60610) also needs the outgoing popup's shared edit slot cleared
 * synchronously: its autocomplete input is an `<input>`, so `commitActiveEditor`
 * only blurs it and leaves the slot claimed — both cells can stay visually open
 * until the deferred activation runs. Clearing the slot here lets the outgoing
 * cell unmount its editor (and multiselect's slot-loss effect commit pending
 * changes) before the microtask opens the target.
 */
export const prepareInlineCellHandoff = (
  getActiveSlot: () => InlineEditSlot | null | undefined,
  clearSlot: () => void,
  targetRowIndex: number,
  targetColumnId: string
): void => {
  commitActiveEditor();
  const activeSlot = getActiveSlot();
  if (activeSlot?.rowIndex == null) return;
  const switchingCells =
    activeSlot.rowIndex !== targetRowIndex || activeSlot.columnId !== targetColumnId;
  if (switchingCells) {
    // Flush so the outgoing popup unmounts before the deferred activation microtask.
    flushSync(() => clearSlot());
  }
};
